import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Search, PlusSquare, User, LogOut, Menu, X, Home, Bookmark, Award, Info, Shield, Hash } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

interface SearchResult {
  id: string;
  username: string;
  photo_url: string | null;
  bio: string | null;
}

interface TrendingTopic {
  topic: string;
  post_count: number;
  engagement_score: number;
}

const Navbar: React.FC = () => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [trendingTopics, setTrendingTopics] = useState<TrendingTopic[]>([]);
  const [isLoadingTopics, setIsLoadingTopics] = useState(true);
  const [topicsError, setTopicsError] = useState<string | null>(null);
  const searchResultsRef = useRef<HTMLDivElement>(null);

  const handleSearchResultClick = (username: string) => {
    navigate(`/home/profile/${username.toLowerCase()}`);
    setSearchQuery('');
    setSearchResults([]);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const toggleMenu = () => setIsMenuOpen(prev => !prev);

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchResultsRef.current && !searchResultsRef.current.contains(event.target as Node)) {
        setSearchResults([]);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const fetchTrendingTopics = async () => {
      try {
        setTopicsError(null);

        const { data, error: fetchError } = await supabase
          .from('trending_topics')
          .select('*')
          .order('engagement_score', { ascending: false })
          .limit(5);

        if (fetchError) {
          throw fetchError;
        }

        setTrendingTopics(data || []);
      } catch (err) {
        console.error('Error fetching trending topics:', err);
        setTopicsError('Failed to load trending topics');
        setTrendingTopics([]);
      } finally {
        setIsLoadingTopics(false);
      }
    };

    fetchTrendingTopics();

    const subscription = supabase
      .channel('trending_topics_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'trending_topics'
        },
        () => {
          fetchTrendingTopics();
        }
      )
      .subscribe();

    const interval = setInterval(fetchTrendingTopics, 5 * 60 * 1000);

    return () => {
      subscription.unsubscribe();
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    const searchUsers = async () => {
      if (!searchQuery.trim()) {
        setSearchResults([]);
        return;
      }

      setIsSearching(true);
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('id, username, photo_url, bio')
          .ilike('username', `%${searchQuery}%`)
          .limit(5);

        if (error) throw error;
        setSearchResults(data || []);
      } catch (error) {
        console.error('Error searching users:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    };

    const debounceTimeout = setTimeout(searchUsers, 300);
    return () => clearTimeout(debounceTimeout);
  }, [searchQuery]);

  return (
    <>
      <nav className="fixed w-full backdrop-blur-md bg-[#272727] border-b border-gray-700 h-16 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full">
          <div className="flex items-center justify-between h-full">
            <div className="flex items-center flex-shrink-0">
              <Link to="/" className="flex items-center font-bold text-white whitespace-nowrap">
                <svg className="h-8 w-8 text-white mr-2" viewBox="0 0 902.4 956.58" fill="currentColor">
                  <path d="M898.15,134.66c-16.65-48.66-89.26-85.33-89.26-85.33C665.12-25.34,602,6.94,602,6.94c-25.9,7.28-48.67,22.39-66.52,37.65h0c-22.71,18.91-64.14,63.84-64.14,63.84,7.07-12.9,13.69-25.3,22.14-37.36,1.42-1.98,2.89-3.98,4.34-5.97-35.87-3.62-70.22,.02-104.27,10.09-34.05,10.07-64.99,25.7-93.13,48.24,2.3,.88,4.61,1.76,6.89,2.65,13.66,5.53,26.08,12.32,39.02,19.3-.02,0-.03,0-.05-.01-1.62-.41-59.37-15.17-88.34-18.67-23.31-3.12-50.7-3.44-76.46,4.55,0,0-70.53,7.22-150.6,148.06,0,0-41,70.25-28.52,120.15,0,0,8.62,33.78,67.55,99.47,0,0,73.95,85.77,158.5,147.52,0,0,71.38,60.19,147.47,79.48,0,0,48.07,15.49,73.06-20.62v.02c.08-.11,.19-.28,.26-.39,.2-.29,.41-.56,.6-.85,0,0,5.59-8.5,12.42-21.23,4.99-8.89,10.29-18.57,14.44-26.21,1.32-2.49,2.67-4.96,4.03-7.44,.46-.84,.89-1.64,1.26-2.34v.04c4.57-8.23,9.34-16.37,14.41-24.37-1.35,5.24-2.79,10.45-4.32,15.63-6.3,21.35-11.05,43.14-13.53,65.27-1.34,11.97-2.48,23.95-2.25,35.96,.67,36.15,6.62,72.05,20.85,105.45,13.29,31.18,37.98,64.06,65.16,84.35,42.63,31.81,103.6,35.6,151.25,13.13,17.58-8.29,35.94-20.74,46.41-37.29,12.38-19.55,18.79-42.67,19.31-65.74,.46-20.4-3.56-40.76-10.84-59.78-6.96-18.19-21.25-33.14-25.2-52.48-2.45-12.02-.37-24.46,3.84-35.83v-.03c1.85-4.87-.29-10.34-4.95-12.67l-51.26-25.59c-4.57-2.28-10.18-.82-12.9,3.5-24,38.06-28.38,86.31-5.35,127.51,8.29,14.83,23.46,35.45,10.27,51.97-5.25,6.58-13.61,9.82-22.02,9.46-24.64-1.08-41.85-23.36-47.58-46.39-10.76-43.33,1.92-87.14,14.79-128.32,5.36-17.17,10.49-34.46,14.56-51.96-8.18-6.46-16.32-13.37-24.99-21.35,12.96,5.96,25.53,12.53,37.86,19.49,3.88,2.08,7.83,4.18,11.63,6.18,12.23,6.69,21.14,10.62,21.14,10.62,.17,.07,.33,.13,.5,.2,.27,.12,.63,.28,.87,.38v-.04c40.6,16.76,72.55-22.4,72.55-22.4,53.38-57.56,80.56-146.88,80.56-146.88,37.4-97.79,52.82-209.98,52.82-209.98,13.74-87.17,2.61-120.21,2.61-120.21Zm-265.86,306.51c-50.87,7.45-100.13-17.81-124.13-63.27-10.35-19.6-14.59-40.1-14.08-59.96-9.35-2.55-19.27-2.49-28.69,.3-9.42,2.79-17.78,8.13-24.24,15.35,11.23,16.39,18.81,35.89,20.78,57.96,4.58,51.2-23.02,99.18-69.76,120.6-72.35,33.15-155.24-10.11-170.28-87.15-11.33-58.05,21.78-116.5,77.32-136.82,41.08-15.03,84.35-6.39,116.07,18.13,10.77-11.24,24.29-19.62,39.45-24.1,15.16-4.48,31.07-4.8,46.22-1.23,13.29-37.82,44.9-68.6,87.55-78.32,57.66-13.15,117.23,17.9,139.28,72.78,29.27,72.83-16.77,154.2-95.5,165.73Z"/>
                  <path d="M327.95,319.32c-54.42,8.85-85.64,66.26-63.48,116.75,15.27,34.78,52.17,54.85,89.67,48.75,54.43-8.85,85.64-66.26,63.48-116.75-15.27-34.78-52.17-54.85-89.67-48.75Z"/>
                  <path d="M602.23,238.24c-54.43,8.85-85.64,66.26-63.48,116.75,15.27,34.78,52.17,54.85,89.67,48.75,54.43-8.85,85.64-66.26,63.47-116.75-15.27-34.78-52.17-54.85-89.67-48.75Z"/>
                </svg>
                <span className="text-xl">Spectacle</span>
              </Link>
            </div>

            <div className="hidden md:block flex-1 max-w-md mx-8">
              <div className="relative">
                <form className="relative" onSubmit={(e) => e.preventDefault()}>
                  <input
                    id="search"
                    name="search"
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search journalists, stories..."
                    className="w-full bg-[#272727] text-white rounded-full py-2 pl-10 pr-4 focus:outline-none focus:ring-2 focus:ring-gray-600"
                    autoComplete="off"
                    maxLength={100}
                  />
                  <Search className="absolute left-3 top-2.5 text-gray-400 h-5 w-5" />
                  {isSearching && (
                    <div className="absolute right-3 top-2.5">
                      <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white" aria-label="Searching..."></div>
                    </div>
                  )}
                </form>

                {searchResults.length > 0 && (
                  <div
                    ref={searchResultsRef}
                    className="absolute w-full mt-2 bg-[#272727] backdrop-blur-xl rounded-lg shadow-lg border border-gray-700 overflow-hidden z-50"
                  >
                    {searchResults.map((result) => (
                      <button
                        key={result.id}
                        onClick={() => handleSearchResultClick(result.username)}
                        className="w-full flex items-center space-x-3 p-3 hover:bg-[#404040] transition-colors"
                      >
                        <img
                          src={result.photo_url}
                          alt={result.username}
                          className="w-10 h-10 rounded-full object-cover border border-gray-700"
                        />
                        <div className="flex-1 text-left">
                          <div className="font-medium text-white">{result.username.toLowerCase()}</div>
                          {result.bio && (
                            <p className="text-sm text-gray-400 truncate">{result.bio}</p>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="hidden md:flex items-center space-x-4">
              <Link
                to="/home/create"
                className="text-white hover:text-gray-300 transition-colors backdrop-blur-[50%] bg-[#272727] hover:bg-[#404040] p-2 rounded-md"
                title="Create Post"
                aria-label="Create new post"
              >
                <PlusSquare className="h-6 w-6" />
              </Link>

              {currentUser && (
                <>
                  <Link
                    to={`/home/profile/${currentUser.username.toLowerCase()}`}
                    className="text-white hover:text-gray-300 transition-colors backdrop-blur-[50%] bg-[#272727] hover:bg-[#404040] p-2 rounded-md"
                    title="Profile"
                    aria-label="View profile"
                  >
                    <User className="h-6 w-6" />
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="text-white hover:text-gray-300 transition-colors backdrop-blur-[50%] bg-[#272727] hover:bg-[#404040] p-2 rounded-md"
                    title="Logout"
                    aria-label="Sign out"
                  >
                    <LogOut className="h-6 w-6" />
                  </button>
                </>
              )}
            </div>

            <div className="md:hidden z-50">
              <button onClick={toggleMenu} className="text-white p-1 relative z-50">
                {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {isMenuOpen && (
        <div className="md:hidden fixed inset-0 z-40 pt-16">
          <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" onClick={toggleMenu}></div>
          <div className="fixed inset-x-0 top-16 bottom-0 bg-[#272727] border-t border-gray-700 overflow-y-auto z-40">
            <div className="px-4 pt-2 pb-3 space-y-1">
              <div className="relative mb-4">
                <input
                  id="mobile-search"
                  name="mobile-search"
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search..."
                  className="w-full bg-[#272727] text-white rounded-full py-2 pl-10 pr-4 focus:outline-none focus:ring-2 focus:ring-gray-600"
                  autoComplete="off"
                  maxLength={100}
                />
                <Search className="absolute left-3 top-2.5 text-gray-400 h-5 w-5" />
                {isSearching && (
                  <div className="absolute right-3 top-2.5">
                    <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white" aria-label="Searching..."></div>
                  </div>
                )}

                {searchResults.length > 0 && (
                  <div className="absolute w-full mt-2 bg-[#272727] backdrop-blur-xl rounded-lg shadow-lg border border-gray-700 overflow-hidden z-50">
                    {searchResults.map((result) => (
                      <button
                        key={result.id}
                        onClick={() => {
                          handleSearchResultClick(result.username);
                          toggleMenu();
                        }}
                        className="w-full flex items-center space-x-3 p-3 hover:bg-[#404040] transition-colors"
                      >
                        <img
                          src={result.photo_url}
                          alt={result.username}
                          className="w-10 h-10 rounded-full object-cover border border-gray-700"
                        />
                        <div className="flex-1 text-left">
                          <div className="font-medium text-white">{result.username.toLowerCase()}</div>
                          {result.bio && (
                            <p className="text-sm text-gray-400 truncate">{result.bio}</p>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="border-b border-gray-800 pb-2 mb-2"></div>

              <Link
                to="/home"
                className={`flex items-center px-3 py-3 rounded-md transition-colors w-full ${
                  isActive('/home')
                    ? 'bg-[#404040] text-white'
                    : 'text-gray-300 hover:bg-[#404040] hover:text-white'
                }`}
                onClick={() => setIsMenuOpen(false)}
              >
                <Home className="h-5 w-5 mr-3" />
                <span>Home</span>
              </Link>

              <Link
                to="/home/bookmarks"
                className={`flex items-center px-3 py-3 rounded-md transition-colors w-full ${
                  isActive('/home/bookmarks')
                    ? 'bg-[#404040] text-white'
                    : 'text-gray-300 hover:bg-[#404040] hover:text-white'
                }`}
                onClick={() => setIsMenuOpen(false)}
              >
                <Bookmark className="h-5 w-5 mr-3" />
                <span>Saved Stories</span>
              </Link>

              <Link
                to="/home/verified"
                className={`flex items-center px-3 py-3 rounded-md transition-colors w-full ${
                  isActive('/home/verified')
                    ? 'bg-[#404040] text-white'
                    : 'text-gray-300 hover:bg-[#404040] hover:text-white'
                }`}
                onClick={() => setIsMenuOpen(false)}
              >
                <Award className="h-5 w-5 mr-3" />
                <span>Verified Journalists</span>
              </Link>

              {currentUser?.is_admin && (
                <Link
                  to="/home/admin"
                  className={`flex items-center px-3 py-3 rounded-md transition-colors w-full ${
                    isActive('/home/admin')
                      ? 'bg-purple-500/20 text-purple-400 border border-purple-500/50'
                      : 'text-gray-300 hover:bg-[#404040] hover:text-white'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  <Shield className="h-5 w-5 mr-3" />
                  <span>Admin Dashboard</span>
                </Link>
              )}

              <div className="border-b border-gray-800 my-4"></div>

              <div className="mb-4">
                <h3 className="px-3 text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                  Trending Topics
                </h3>
                <div className="space-y-1">
                  {isLoadingTopics ? (
                    <div className="px-3 py-2 text-gray-500 text-sm">
                      Loading topics...
                    </div>
                  ) : topicsError ? (
                    <div className="px-3 py-2 text-red-400 text-sm">
                      {topicsError}
                      <button
                        onClick={() => window.location.reload()}
                        className="block mt-1 text-xs underline hover:no-underline"
                      >
                        Retry
                      </button>
                    </div>
                  ) : trendingTopics.length === 0 ? (
                    <div className="px-3 py-2 text-gray-500 text-sm">
                      No trending topics yet
                    </div>
                  ) : (
                    trendingTopics.map((topic) => (
                      <Link
                        key={topic.topic}
                        to={`/home/topic/${topic.topic.toLowerCase()}`}
                        className="flex items-center px-3 py-2 text-gray-300 hover:bg-[#404040] hover:text-white rounded-md text-sm group"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        <Hash className="h-4 w-4 mr-2 text-gray-500 group-hover:text-gray-300" />
                        <span className="truncate">{topic.topic}</span>
                        <span className="ml-auto text-xs text-gray-500 group-hover:text-gray-400 transition-colors">
                          {topic.post_count} {topic.post_count === 1 ? 'post' : 'posts'}
                        </span>
                      </Link>
                    ))
                  )}
                </div>
              </div>

              <div className="border-b border-gray-800 my-4"></div>

              <Link
                to="/home/create"
                className={`flex items-center px-3 py-3 rounded-md transition-colors w-full ${
                  isActive('/home/create')
                    ? 'bg-[#404040] text-white'
                    : 'text-gray-300 hover:bg-[#404040] hover:text-white'
                }`}
                onClick={() => setIsMenuOpen(false)}
              >
                <PlusSquare className="h-5 w-5 mr-3" />
                <span>Create Post</span>
              </Link>

              {currentUser && (
                <>
                  <Link
                    to={`/home/profile/${currentUser.username.toLowerCase()}`}
                    className={`flex items-center px-3 py-3 rounded-md transition-colors w-full ${
                      location.pathname.includes(`/home/profile/${currentUser.username.toLowerCase()}`)
                        ? 'bg-[#404040] text-white'
                        : 'text-gray-300 hover:bg-[#404040] hover:text-white'
                    }`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <User className="h-5 w-5 mr-3" />
                    <span>Profile</span>
                  </Link>

                  <Link
                    to="/home/about"
                    className={`flex items-center px-3 py-3 rounded-md transition-colors w-full ${
                      isActive('/home/about')
                        ? 'bg-[#404040] text-white'
                        : 'text-gray-300 hover:bg-[#404040] hover:text-white'
                    }`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <Info className="h-5 w-5 mr-3" />
                    <span>About</span>
                  </Link>

                  <button
                    onClick={() => {
                      handleLogout();
                      setIsMenuOpen(false);
                    }}
                    className="flex items-center px-3 py-3 text-gray-300 hover:bg-[#404040] hover:text-white rounded-md w-full text-left transition-colors"
                  >
                    <LogOut className="h-5 w-5 mr-3" />
                    <span>Logout</span>
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;